import { useState, useCallback, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const DEFAULT_CATEGORIES = ['Branding', 'Digital Marketing', 'Website', 'Media Production', 'Event', 'Graphic Design'];

const CAT_COLORS = [
  '#1d4ed8','#15803d','#7e22ce','#b91c1c','#a16207','#c2410c',
  '#0e7490','#b45309','#4f46e5','#be185d','#047857','#7c3aed',
];

function catColor(idx) { return CAT_COLORS[idx % CAT_COLORS.length]; }

const BLANK = { title: '', category: '', description: '', images: [] };

function getImages(item) {
  return item.images?.length ? item.images : item.imageUrl ? [item.imageUrl] : [];
}

function UploadSpinner() {
  return <span style={{ display:'inline-block',width:13,height:13,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite',flexShrink:0 }} />;
}

const label = { fontSize:'0.72rem',fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:6 };
const input = { width:'100%',padding:'0.6rem 0.8rem',border:'1.5px solid #e5e7eb',borderRadius:8,fontSize:'0.875rem',fontFamily:'inherit',outline:'none',boxSizing:'border-box',background:'#fff' };

export default function AdminPortfolio({ items, setItems }) {
  const [categories, setCategories]   = useState(DEFAULT_CATEGORIES);
  const [editing, setEditing]         = useState(null);
  const [addFor, setAddFor]           = useState(null);
  const [form, setForm]               = useState(BLANK);
  const [uploading, setUploading]     = useState(false);
  const [uploadPct, setUploadPct]     = useState(0);
  const [saving, setSaving]           = useState(false);
  const [toast, setToast]             = useState(null);
  const [previewImg, setPreviewImg]   = useState(null);
  const [searchQ, setSearchQ]         = useState('');
  const [newCat, setNewCat]           = useState('');
  const [addingCat, setAddingCat]     = useState(false);

  // Load custom categories from Firestore
  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'portfolioSettings')).then(snap => {
      if (snap.exists() && snap.data().categories?.length) {
        setCategories(snap.data().categories);
      }
    });
  }, []);

  const saveCategories = async (cats) => {
    await setDoc(doc(db, 'siteContent', 'portfolioSettings'), { categories: cats }, { merge: true });
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name || categories.includes(name)) return;
    const updated = [...categories, name];
    setCategories(updated);
    await saveCategories(updated);
    setNewCat('');
    setAddingCat(false);
    showToast(`Category "${name}" added`);
  };

  const removeCategory = async (cat) => {
    const count = items.filter(i => i.category === cat).length;
    if (count > 0 && !window.confirm(`"${cat}" has ${count} item(s). Delete category anyway?`)) return;
    const updated = categories.filter(c => c !== cat);
    setCategories(updated);
    await saveCategories(updated);
    showToast(`Category "${cat}" removed`);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const uploadImages = useCallback(async (files, id) => {
    setUploading(true); setUploadPct(0);
    const total = files.length; let done = 0;
    const urls = await Promise.all(Array.from(files).map(async file => {
      const r = ref(storage, `portfolio/${id || 'new'}/${Date.now()}_${file.name}`);
      await uploadBytes(r, file);
      done++;
      setUploadPct(Math.round((done / total) * 100));
      return getDownloadURL(r);
    }));
    setUploading(false); setUploadPct(0);
    return urls;
  }, []);

  const openAdd = cat => { setForm({ ...BLANK, category: cat }); setAddFor(cat); };

  const add = async () => {
    if (!form.title.trim()) return showToast('Title is required', 'error');
    setSaving(true);
    try {
      const data = { title: form.title.trim(), category: form.category, description: form.description.trim(), images: form.images, createdAt: serverTimestamp() };
      const docRef = await addDoc(collection(db, 'portfolio'), data);
      setItems(p => [{ id: docRef.id, ...data }, ...p]);
      setForm(BLANK); setAddFor(null);
      showToast('Item added!');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
  };

  const save = async () => {
    if (!editing.title.trim()) return showToast('Title is required', 'error');
    setSaving(true);
    try {
      const { id } = editing;
      const payload = { title: editing.title.trim(), category: editing.category, description: (editing.description||'').trim(), images: editing.images || [] };
      await updateDoc(doc(db, 'portfolio', id), payload);
      setItems(p => p.map(i => i.id === id ? { ...editing, ...payload } : i));
      setEditing(null);
      showToast('Saved!');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
  };

  const remove = async item => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await deleteDoc(doc(db, 'portfolio', item.id));
    setItems(p => p.filter(i => i.id !== item.id));
    if (editing?.id === item.id) setEditing(null);
    showToast('Deleted');
  };

  const removeImg = (setter, idx) => setter(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));

  const ImageStrip = ({ target, setter, id }) => {
    const imgs = getImages(target);
    return (
      <div>
        <div style={{ fontSize:'0.72rem',fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:10 }}>
          Images ({imgs.length})
        </div>
        {imgs.length > 0 && (
          <div style={{ display:'flex',flexWrap:'wrap',gap:8,marginBottom:12 }}>
            {imgs.map((url, i) => (
              <div key={i} style={{ position:'relative',width:80,height:80,borderRadius:8,overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.12)',cursor:'pointer',flexShrink:0 }}
                onClick={() => setPreviewImg(url)}>
                <img src={url} alt="" style={{ width:'100%',height:'100%',objectFit:'cover',display:'block' }} />
                <button onClick={e => { e.stopPropagation(); removeImg(setter, i); }}
                  style={{ position:'absolute',top:3,right:3,background:'rgba(220,38,38,0.9)',color:'#fff',border:'none',width:20,height:20,borderRadius:'50%',fontSize:'0.6rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <label style={{ display:'inline-flex',alignItems:'center',gap:6,background:uploading?'#9ca3af':'#1A3A8F',color:'#fff',padding:'0.45rem 1rem',borderRadius:8,fontSize:'0.78rem',fontWeight:700,cursor:uploading?'not-allowed':'pointer',userSelect:'none' }}>
          {uploading ? <><UploadSpinner /> Uploading {uploadPct}%</> : <><span>📷</span> Add Photos</>}
          <input type="file" accept="image/*" multiple style={{ display:'none' }} disabled={uploading}
            onChange={async e => {
              if (!e.target.files.length) return;
              const urls = await uploadImages(e.target.files, id);
              setter(p => ({ ...p, images: [...getImages(p), ...urls] }));
              e.target.value = '';
            }} />
        </label>
      </div>
    );
  };

  return (
    <div style={{ minHeight:'100vh' }}>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes toastIn { from { opacity:0; transform:translateX(120%); } to { opacity:1; transform:translateX(0); } }
        .pf-img-thumb:hover img { transform: scale(1.07); }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed',top:24,right:24,zIndex:9999,background:toast.type==='error'?'#dc2626':'#16a34a',color:'#fff',padding:'0.75rem 1.25rem',borderRadius:10,fontSize:'0.85rem',fontWeight:600,boxShadow:'0 8px 24px rgba(0,0,0,0.2)',animation:'toastIn 0.3s ease',display:'flex',alignItems:'center',gap:8 }}>
          {toast.type==='error'?'✕':'✓'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'1.75rem',flexWrap:'wrap',gap:'1rem' }}>
        <div>
          <h1 style={{ margin:0,fontSize:'1.75rem',fontWeight:800 }}>
            Portfolio
            <span style={{ background:'#F5A623',color:'#fff',fontSize:'0.85rem',padding:'2px 10px',borderRadius:20,fontWeight:700,verticalAlign:'middle',marginLeft:8 }}>{items.length}</span>
          </h1>
          <p style={{ margin:'0.3rem 0 0',fontSize:'0.82rem',color:'#9ca3af' }}>Manage categories and images — click any photo to preview</p>
        </div>
        <div style={{ display:'flex',gap:10,alignItems:'center',flexWrap:'wrap' }}>
          <div style={{ position:'relative' }}>
            <span style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',fontSize:'0.85rem',color:'#9ca3af' }}>🔍</span>
            <input placeholder="Search…" value={searchQ} onChange={e => setSearchQ(e.target.value)}
              style={{ ...input, paddingLeft:34,paddingRight:12,paddingTop:9,paddingBottom:9,width:200 }} />
          </div>
          <button onClick={() => setAddingCat(v => !v)}
            style={{ background:addingCat?'#6b7280':'#1A3A8F',color:'#fff',border:'none',padding:'0.5rem 1.1rem',borderRadius:8,fontSize:'0.82rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6 }}>
            {addingCat ? '✕ Cancel' : '＋ New Category'}
          </button>
        </div>
      </div>

      {/* Add new category form */}
      {addingCat && (
        <div style={{ background:'#f0f7ff',border:'2px solid #93c5fd',borderRadius:12,padding:'1rem 1.25rem',marginBottom:'1.5rem',display:'flex',gap:10,alignItems:'center',animation:'slideUp 0.2s ease' }}>
          <input
            placeholder="Category name e.g. Photography, Events…"
            value={newCat} onChange={e => setNewCat(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            style={{ ...input, flex:1, maxWidth:360 }}
            autoFocus
          />
          <button onClick={addCategory}
            style={{ background:'#1A3A8F',color:'#fff',border:'none',padding:'0.6rem 1.25rem',borderRadius:8,fontSize:'0.85rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>
            Add Category
          </button>
        </div>
      )}

      {/* Category sections */}
      {categories.map((cat, catIdx) => {
        const color = catColor(catIdx);
        const light = color + '18';
        const catItems = items.filter(i => i.category === cat && (!searchQ || i.title.toLowerCase().includes(searchQ.toLowerCase())));
        const catImages = catItems.flatMap(item => getImages(item).map(url => ({ url, item })));
        const isAddingHere = addFor === cat;

        return (
          <div key={cat} style={{ marginBottom:'2.5rem' }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.5rem' }}>
              <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                <span style={{ width:4,height:28,background:color,borderRadius:4,display:'inline-block' }} />
                <h2 style={{ margin:0,fontSize:'1.05rem',fontWeight:800 }}>{cat}</h2>
                <span style={{ background:light,color:color,fontSize:'0.68rem',fontWeight:700,padding:'2px 9px',borderRadius:20 }}>
                  {catItems.length} item{catItems.length !== 1 ? 's' : ''} · {catImages.length} photo{catImages.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                <button onClick={() => isAddingHere ? setAddFor(null) : openAdd(cat)}
                  style={{ background:isAddingHere?'#6b7280':color,color:'#fff',border:'none',padding:'0.4rem 1rem',borderRadius:8,fontSize:'0.78rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:5 }}>
                  {isAddingHere ? '✕ Cancel' : '+ Add Item'}
                </button>
                <button onClick={() => removeCategory(cat)}
                  style={{ background:'none',border:'1.5px solid #fca5a5',color:'#dc2626',padding:'0.4rem 0.75rem',borderRadius:8,fontSize:'0.75rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}
                  title="Delete this category">
                  🗑
                </button>
              </div>
            </div>

            {/* Add item form */}
            {isAddingHere && (
              <div style={{ background:'#fff',border:`2px solid ${color}`,borderRadius:14,padding:'1.5rem',marginBottom:'1.25rem',animation:'slideUp 0.25s ease',boxShadow:`0 8px 32px ${color}18` }}>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1rem' }}>
                  <div>
                    <label style={label}>Project Title *</label>
                    <input placeholder="e.g. Brand Identity for XYZ" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={input} />
                  </div>
                  <div>
                    <label style={label}>Category</label>
                    <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={input}>
                      {categories.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:'1.1rem' }}>
                  <label style={label}>Description (optional)</label>
                  <textarea rows={2} placeholder="Brief description…" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...input, resize:'none' }} />
                </div>
                <div style={{ background:'#f8faff',borderRadius:10,padding:'1rem',marginBottom:'1.1rem',border:'1px solid #e5eeff' }}>
                  <ImageStrip target={form} setter={setForm} id="new" />
                </div>
                <div style={{ display:'flex',gap:10 }}>
                  <button onClick={add} disabled={saving || uploading}
                    style={{ background:color,color:'#fff',border:'none',padding:'0.6rem 1.5rem',borderRadius:8,fontSize:'0.875rem',fontWeight:700,cursor:saving||uploading?'not-allowed':'pointer',opacity:saving||uploading?0.7:1,fontFamily:'inherit' }}>
                    {saving ? 'Saving…' : '+ Add to Portfolio'}
                  </button>
                  <button onClick={() => setAddFor(null)}
                    style={{ background:'none',border:'1.5px solid #e5e7eb',color:'#6b7280',padding:'0.6rem 1.1rem',borderRadius:8,fontSize:'0.875rem',fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Image grid */}
            {catImages.length === 0 && !isAddingHere ? (
              <div style={{ background:'#fafafa',border:'2px dashed #e5e7eb',borderRadius:12,padding:'2rem',textAlign:'center',cursor:'pointer' }} onClick={() => openAdd(cat)}>
                <div style={{ fontSize:'2rem',marginBottom:6,opacity:0.3 }}>🖼️</div>
                <div style={{ fontSize:'0.8rem',color:'#9ca3af',fontWeight:600 }}>No items yet — click to add one</div>
              </div>
            ) : (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:'0.75rem' }}>
                {catImages.map(({ url, item }, idx) => (
                  <div key={`${item.id}-${idx}`} className="pf-img-thumb"
                    style={{ position:'relative',borderRadius:10,overflow:'hidden',background:'#f0f4ff',cursor:'pointer',aspectRatio:'1',border:'2px solid transparent',transition:'border-color 0.2s' }}
                    onClick={() => setPreviewImg(url)}>
                    <img src={url} alt={item.title} style={{ width:'100%',height:'100%',objectFit:'cover',display:'block',transition:'transform 0.3s' }} />
                    <div style={{ position:'absolute',inset:0,background:'linear-gradient(to top,rgba(0,0,0,0.65) 0%,transparent 55%)',opacity:0,transition:'opacity 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                      <div style={{ position:'absolute',bottom:8,left:8,right:8 }}>
                        <div style={{ color:'#fff',fontSize:'0.68rem',fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginBottom:4 }}>{item.title}</div>
                        <div style={{ display:'flex',gap:4 }}>
                          <button onClick={e => { e.stopPropagation(); setEditing({ ...item, images: getImages(item) }); }}
                            style={{ flex:1,background:'rgba(255,255,255,0.9)',border:'none',borderRadius:5,padding:'3px 0',fontSize:'0.65rem',fontWeight:700,cursor:'pointer',color:'#1A3A8F' }}>✎ Edit</button>
                          <button onClick={e => { e.stopPropagation(); remove(item); }}
                            style={{ flex:1,background:'rgba(220,38,38,0.85)',border:'none',borderRadius:5,padding:'3px 0',fontSize:'0.65rem',fontWeight:700,cursor:'pointer',color:'#fff' }}>Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div onClick={() => openAdd(cat)}
                  style={{ borderRadius:10,border:`2px dashed ${color}55`,background:`${color}0a`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:6,cursor:'pointer',aspectRatio:'1' }}>
                  <span style={{ fontSize:'1.75rem',color:color,opacity:0.6 }}>+</span>
                  <span style={{ fontSize:'0.7rem',fontWeight:700,color:color,opacity:0.8 }}>Add</span>
                </div>
              </div>
            )}

            <div style={{ height:1,background:'#f0f0f0',marginTop:'1.5rem' }} />
          </div>
        );
      })}

      {/* Edit modal */}
      {editing && (
        <div style={{ position:'fixed',inset:0,background:'rgba(10,15,30,0.72)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(6px)' }}
          onClick={() => setEditing(null)}>
          <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:580,maxHeight:'92vh',overflowY:'auto',animation:'slideUp 0.28s ease',boxShadow:'0 32px 80px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:'1.3rem 1.75rem',borderBottom:'1.5px solid #f0f0f0',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:10,borderRadius:'16px 16px 0 0' }}>
              <h2 style={{ margin:0,fontSize:'1.1rem',color:'#1A3A8F',fontWeight:800 }}>Edit Item</h2>
              <div style={{ display:'flex',gap:8 }}>
                <button onClick={() => remove(editing)} style={{ background:'#fef2f2',border:'1.5px solid #fecaca',color:'#dc2626',padding:'0.4rem 0.9rem',borderRadius:8,fontSize:'0.75rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>🗑 Delete</button>
                <button onClick={() => setEditing(null)} style={{ background:'#f3f4f6',border:'none',width:34,height:34,borderRadius:'50%',fontSize:'1rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#374151' }}>✕</button>
              </div>
            </div>
            <div style={{ padding:'1.5rem 1.75rem' }}>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1rem' }}>
                <div>
                  <label style={label}>Title</label>
                  <input value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))} style={input} />
                </div>
                <div>
                  <label style={label}>Category</label>
                  <select value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))} style={input}>
                    {categories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:'1.25rem' }}>
                <label style={label}>Description</label>
                <textarea rows={3} value={editing.description||''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} style={{ ...input, resize:'none' }} />
              </div>
              <div style={{ background:'#f8faff',borderRadius:12,padding:'1.25rem',border:'1px solid #e5eeff',marginBottom:'1.5rem' }}>
                <ImageStrip target={editing} setter={setEditing} id={editing.id} />
              </div>
              <div style={{ display:'flex',gap:10 }}>
                <button onClick={save} disabled={saving||uploading}
                  style={{ flex:1,background:'#1A3A8F',color:'#fff',border:'none',padding:'0.75rem',borderRadius:10,fontSize:'0.9rem',fontWeight:700,cursor:saving||uploading?'not-allowed':'pointer',opacity:saving||uploading?0.7:1,fontFamily:'inherit' }}>
                  {saving ? 'Saving…' : '✓ Save Changes'}
                </button>
                <button onClick={() => setEditing(null)}
                  style={{ background:'none',border:'1.5px solid #e5e7eb',color:'#6b7280',padding:'0.75rem 1.2rem',borderRadius:10,fontSize:'0.9rem',fontWeight:600,cursor:'pointer',fontFamily:'inherit' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewImg && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.93)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1.5rem',animation:'slideUp 0.2s ease' }}
          onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="" style={{ maxWidth:'100%',maxHeight:'90vh',objectFit:'contain',borderRadius:6 }} />
          <button onClick={() => setPreviewImg(null)} style={{ position:'fixed',top:20,right:24,background:'rgba(255,255,255,0.15)',color:'#fff',border:'none',width:42,height:42,borderRadius:'50%',fontSize:'1.1rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
        </div>
      )}
    </div>
  );
}
